import { Sun, Moon, Command, LogOut } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { usePrefs } from "@/lib/prefs-store";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/components/system";

/**
 * SPEC 4.8: 52px desktop topbar.
 *  - Left: breadcrumbs (current section + sub-area)
 *  - Center: ⌘K palette button (BW12.5: shows a "coming soon" toast until the
 *    palette ships — never a silent no-op)
 *  - Right: theme toggle, account menu (mobile only — sidebar owns the
 *    avatar on desktop, BW17 Issue 6).
 */

// BW17 Issue 2: full hierarchy so /pantry → "Haus / Kitchen / Pantry" instead
// of the meaningless "Haus / Haus". Each entry is `path-or-prefix → crumb`.
// Lookup walks segment-by-segment so deep routes like
// /finance/transactions/new also resolve. `:id` segments are dropped — the
// detail page renders its own heading.
const SEGMENT_LABELS: Record<string, string> = {
  // Top-level
  "": "Today",
  calendar: "Calendar",
  health: "Health",
  kitchen: "Kitchen",
  finance: "Finance",
  evidence: "Evidence",
  projects: "Projects",
  documents: "Documents",
  learning: "Learning",
  people: "People",
  threads: "Threads",
  integrations: "Integrations",
  settings: "Settings",
  more: "More",
  // Sub-areas reached directly via the sidebar
  pantry: "Pantry",
  recipes: "Recipes",
  "meal-plan": "Meal plan",
  shopping: "Shopping",
  // Health sub-areas
  body: "Body",
  recovery: "Recovery",
  training: "Training",
  nutrition: "Nutrition",
  "red-s": "RED-S",
  glp1: "GLP-1",
  // Finance sub-areas
  accounts: "Accounts",
  transactions: "Transactions",
  budget: "Budget",
  receipts: "Receipts",
  savings: "Savings",
  investments: "Investments",
  // Calendar sub-areas
  day: "Day",
  week: "Week",
  month: "Month",
  year: "Year",
  // Common leaf actions
  new: "New",
  // Learning sub-areas
  vocabulary: "Vocabulary",
  import: "Import",
  stats: "Stats",
  // Docs
  docs: "Docs",
  garmin: "Garmin",
};

// Routes that visually live inside the Kitchen hub even though they sit at
// the top level for legacy reasons. Inserts a synthetic "Kitchen" crumb so
// the breadcrumb reflects the user's mental model.
const KITCHEN_CHILDREN = new Set(["pantry", "recipes", "meal-plan", "shopping"]);

function buildCrumbs(pathname: string): { label: string; to: string | null }[] {
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) {
    return [{ label: "Today", to: null }];
  }
  const crumbs: { label: string; to: string | null }[] = [];
  if (KITCHEN_CHILDREN.has(segments[0])) {
    crumbs.push({ label: "Kitchen", to: "/kitchen" });
  }
  let acc = "";
  segments.forEach((seg, idx) => {
    acc += "/" + seg;
    // Drop ID-shaped segments — UUIDs, dates, week tokens — they don't carry
    // a stable label and the page itself already shows the entity name.
    if (/^[0-9a-f]{8}-/.test(seg)) return;
    if (/^\d{4}-\d{2}-\d{2}$/.test(seg)) return;
    if (/^\d{4}-W\d{2}$/.test(seg)) return;
    if (/^\d{4}-\d{2}$/.test(seg)) return;
    if (/^\d{4}$/.test(seg)) return;
    const label = SEGMENT_LABELS[seg] ?? seg.replace(/-/g, " ");
    crumbs.push({
      label: label.charAt(0).toUpperCase() + label.slice(1),
      to: idx === segments.length - 1 ? null : acc,
    });
  });
  return crumbs;
}

export function Topbar() {
  const { theme, toggleTheme } = usePrefs();
  const { pathname } = useLocation();
  const crumbs = buildCrumbs(pathname);

  return (
    <header
      style={{
        position: "sticky",
        top: 0,
        minHeight: "var(--topbar-h)",
        height: "calc(var(--topbar-h) + env(safe-area-inset-top))",
        paddingTop: "env(safe-area-inset-top)",
        background: "var(--bg)",
        borderBottom: "1px solid var(--hair)",
        display: "flex",
        alignItems: "center",
        gap: "var(--space-4)",
        zIndex: 10,
        paddingLeft: "var(--space-6)",
        paddingRight: "var(--space-6)",
      }}
    >
      <Breadcrumbs crumbs={crumbs} />

      <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>
        <KbarTrigger />
      </div>

      <div style={{ display: "flex", gap: "var(--space-1)", alignItems: "center" }}>
        <IconButton
          ariaLabel={
            theme === "dark" ? "Switch to light theme" : "Switch to dark theme"
          }
          onClick={toggleTheme}
        >
          {theme === "dark" ? (
            <Sun size={18} strokeWidth={1.5} />
          ) : (
            <Moon size={18} strokeWidth={1.5} />
          )}
        </IconButton>
        {/*
          BW17 Issue 6: Sidebar already shows a labelled user chip on desktop
          (>=1024px) so the topbar avatar would be a duplicate. Hide the
          topbar version when the sidebar is visible and let the sidebar own
          the avatar; show it again on tablet/mobile where the sidebar
          collapses out of view.
        */}
        <div className="haus-topbar-user">
          <UserMenuButton />
        </div>
      </div>
      <style>{`
        .haus-topbar-user { display: none; }
        @media (max-width: 1023px) {
          .haus-topbar-user { display: inline-flex; }
        }
      `}</style>
    </header>
  );
}

function UserMenuButton() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  async function handleSignOut() {
    setOpen(false);
    await supabase.auth.signOut();
    navigate("/sign-in", { replace: true });
  }

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        title="Account"
        style={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          background: "var(--accent-wash)",
          color: "var(--accent)",
          fontFamily: "var(--font-serif)",
          fontStyle: "italic",
          fontWeight: 500,
          fontSize: 14,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          border: "1px solid transparent",
          cursor: "pointer",
        }}
      >
        J
      </button>
      {open && (
        <div
          role="menu"
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            minWidth: 180,
            background: "var(--bg-3)",
            border: "1px solid var(--hair-2)",
            borderRadius: "var(--radius-card)",
            boxShadow: "0 12px 40px rgba(0,0,0,0.18)",
            overflow: "hidden",
            zIndex: 30,
          }}
        >
          <Link
            to="/settings"
            role="menuitem"
            onClick={() => setOpen(false)}
            style={{
              display: "block",
              padding: "var(--space-2) var(--space-3)",
              fontSize: "var(--type-body-s)",
              color: "var(--fg)",
              textDecoration: "none",
            }}
          >
            View profile
          </Link>
          <button
            type="button"
            role="menuitem"
            onClick={handleSignOut}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--space-2)",
              width: "100%",
              padding: "var(--space-2) var(--space-3)",
              background: "transparent",
              border: "none",
              borderTop: "1px solid var(--hair)",
              fontSize: "var(--type-body-s)",
              color: "var(--severity-bad, #c53e2e)",
              cursor: "pointer",
              textAlign: "left",
            }}
          >
            <LogOut size={14} strokeWidth={1.5} />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function Breadcrumbs({
  crumbs,
}: {
  crumbs: { label: string; to: string | null }[];
}) {
  return (
    <nav
      aria-label="Breadcrumb"
      className="haus-breadcrumbs"
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--space-2)",
        color: "var(--fg-2)",
        fontSize: "var(--type-body-s)",
        minWidth: 0,
        overflow: "hidden",
        whiteSpace: "nowrap",
      }}
    >
      <Link
        to="/"
        style={{
          color: "var(--fg-2)",
          fontFamily: "var(--font-serif)",
          fontStyle: "italic",
          fontSize: "var(--type-body)",
        }}
      >
        Haus
      </Link>
      {crumbs.map((c, i) => {
        const isLast = i === crumbs.length - 1;
        return (
          <span key={`${c.label}-${i}`} style={{ display: "inline-flex", alignItems: "center", gap: "var(--space-2)" }}>
            <span style={{ color: "var(--fg-4)" }}>/</span>
            {c.to && !isLast ? (
              <Link
                to={c.to}
                style={{ color: "var(--fg-2)", textDecoration: "none" }}
              >
                {c.label}
              </Link>
            ) : (
              <span
                style={{ color: "var(--fg)", fontWeight: 500 }}
                aria-current={isLast ? "page" : undefined}
              >
                {c.label}
              </span>
            )}
          </span>
        );
      })}
    </nav>
  );
}

function KbarTrigger() {
  const toast = useToast();
  return (
    <button
      type="button"
      aria-label="Open command palette"
      // Real ⌘K palette ships in BW13. Until then the trigger toasts so the
      // user knows their tap registered instead of silently no-op'ing.
      onClick={() =>
        toast.show("Command palette ships next week — search lives here.")
      }
      className="haus-kbar-trigger"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "var(--space-2)",
        height: 32,
        padding: "0 var(--space-3)",
        background: "var(--chip)",
        border: "1px solid var(--hair)",
        borderRadius: "var(--radius-card)",
        color: "var(--fg-3)",
        fontSize: "var(--type-body-s)",
      }}
    >
      <Command size={14} strokeWidth={1.5} className="haus-kbar-glyph" />
      <span className="haus-kbar-text">Search · jump to · run</span>
      <span className="haus-kbar-text-mobile" aria-hidden>
        Search
      </span>
      <kbd
        className="mono haus-kbar-shortcut"
        style={{
          fontSize: "var(--type-mono-meta)",
          color: "var(--fg-3)",
          padding: "1px 6px",
          border: "1px solid var(--hair-2)",
          borderRadius: 4,
        }}
      >
        ⌘K
      </kbd>
      <style>{`
        .haus-kbar-text-mobile { display: none; }
        @media (max-width: 767px) {
          .haus-kbar-text { display: none; }
          .haus-kbar-shortcut { display: none; }
          .haus-kbar-text-mobile { display: inline; }
        }
        @media (hover: none) {
          .haus-kbar-shortcut { display: none; }
        }
      `}</style>
    </button>
  );
}

function IconButton({
  ariaLabel,
  onClick,
  children,
}: {
  ariaLabel: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      title={ariaLabel}
      onClick={onClick}
      style={{
        width: 32,
        height: 32,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: "transparent",
        color: "var(--fg-2)",
        border: "1px solid transparent",
        borderRadius: "var(--radius-card)",
        transition: `background var(--dur-fast) var(--ease-out), color var(--dur-fast) var(--ease-out)`,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "var(--chip)";
        e.currentTarget.style.color = "var(--fg)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
        e.currentTarget.style.color = "var(--fg-2)";
      }}
    >
      {children}
    </button>
  );
}
